import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, InputGroup, Modal, Row } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { FaTimes, FaUpload } from 'react-icons/fa';
import { UserService } from '../UserService';
import { PlatformService } from '../../platform/PlatformService';
import { IAdvancedSocialPlatform } from '../../../model/user/IAdvancedSocialPlatform';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';
import { BlockingOverlay } from '../../../shared/components/BlockingOverlay';
import { getCountryName } from '../../../utils/helpers/CorrectCountryNames';
import { ErrorToast, SuccessToast, WarningToast } from '../../../utils/toasters';

interface IManualSocialModal {
    show: boolean;
    userId: number;
    platform: IAdvancedSocialPlatform | null;
    onHide: () => void;
    onSaved: () => void;
}

const COUNTRY_POSITIONS = [1, 2, 3, 4, 5];
const METRIC_KEYS = [
    'followers',
    'subscribers',
    'averageLikes',
    'averageComments',
    'averageViews',
    'averageReelPlays',
    'totalPosts',
    'engagementRate',
    'averagePostEngagements',
    'averagePostImpressions',
    'averageConcurrentViewers',
    'viewMinutes',
    'liveHoursPerMonth',
    'totalWatchTime',
    'uniqueVisitors',
    'totalPageViews',
    'newsletterSubscribers',
    'averageDownloads',
    'averagePlays',
    'totalEpisodes',
    'allPlatforms',
];
const EXTERNAL_LINK_PLATFORMS = ['blog', 'podcast', 'linkedin'];

const labelFromKey = (key: string) => {
    const text = key.replace(/([A-Z])/g, ' $1').toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const ageLabel = (metric: string) => metric.replace('age_', '').replace('_or_more', '+').replace(/_/g, ' - ');

const toNumber = (value: string) => (value === '' ? 0 : Math.min(Math.max(Number(value), 0), 100));

export function ManualSocialModal({ show, userId, platform, onHide, onSaved }: IManualSocialModal) {
    const { country, audienceMetricAge } = useSelector((state: any) => state.enums);
    const isUpdate = platform?.status === 'approved' && !!platform?.username;

    const [meta, setMeta] = useState<any>(null);
    const [title, setTitle] = useState('');
    const [externalLink, setExternalLink] = useState('');
    const [metrics, setMetrics] = useState<Record<string, string>>({});
    const [male, setMale] = useState('');
    const [female, setFemale] = useState('');
    const [countries, setCountries] = useState<{ country: string; value: string }[]>(
        COUNTRY_POSITIONS.map(() => ({ country: '', value: '' }))
    );
    const [ages, setAges] = useState<Record<string, string>>({});
    const [file, setFile] = useState<File | null>(null);
    const [existingFile, setExistingFile] = useState<{ path: string; originalName: string } | null>(null);
    const [fileError, setFileError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: 'image/jpeg, image/png, image/jpg',
        multiple: false,
        onDropAccepted: files => {
            setFileError('');
            setFile(files[0]);
        },
        onDropRejected: () => setFileError('Only JPG and PNG images are allowed.'),
    });

    useEffect(() => {
        if (!show || !platform) return;
        setMeta(null);
        setTitle('');
        setExternalLink('');
        setMetrics({});
        setMale('');
        setFemale('');
        setCountries(COUNTRY_POSITIONS.map(() => ({ country: '', value: '' })));
        setAges({});
        setFile(null);
        setExistingFile(null);
        setFileError('');

        PlatformService.getPlatform(platform.id)
            .then(setMeta)
            .catch(err => ErrorToast(err));

        if (isUpdate) {
            UserService.getAdvancedAudience(userId, platform.id)
                .then(data => {
                    setTitle(data?.title ?? '');
                    setExternalLink(data?.externalLink ?? '');
                    const loadedMetrics: Record<string, string> = {};
                    METRIC_KEYS.forEach(key => {
                        if (data?.[key] !== undefined && data?.[key] !== null) {
                            loadedMetrics[key] = String(data[key]);
                        }
                    });
                    setMetrics(loadedMetrics);
                    const audiences: any[] = data?.audiences ?? [];
                    const find = (dimension: string, metric: string) =>
                        audiences.find(item => item.dimension === dimension && item.metric === metric);
                    setMale(find('gender', 'male') ? String(Math.round(find('gender', 'male').value)) : '');
                    setFemale(find('gender', 'female') ? String(Math.round(find('gender', 'female').value)) : '');
                    const loadedCountries = COUNTRY_POSITIONS.map(position => {
                        const item = audiences.find(
                            audience => audience.dimension === 'country' && audience.position === `position-${position}`
                        );
                        return item
                            ? { country: item.metric ?? '', value: String(Math.round(item.value ?? 0)) }
                            : { country: '', value: '' };
                    });
                    setCountries(loadedCountries);
                    const loadedAges: Record<string, string> = {};
                    audiences
                        .filter(item => item.dimension === 'age')
                        .forEach(item => {
                            loadedAges[item.metric] = String(Math.round(item.value ?? 0));
                        });
                    setAges(loadedAges);
                    setExistingFile(data?.reportFile ?? null);
                })
                .catch(err => ErrorToast(err));
        }
    }, [show, platform?.id]);

    const visibleMetrics = useMemo(() => METRIC_KEYS.filter(key => meta?.[key]), [meta]);
    const hasExternalLink = EXTERNAL_LINK_PLATFORMS.includes(meta?.platformEnum ?? '');

    const setGender = (which: 'male' | 'female', value: string) => {
        const number = toNumber(value);
        if (which === 'male') {
            setMale(value === '' ? '' : String(number));
            setFemale(number > 0 ? String(100 - number) : '');
        } else {
            setFemale(value === '' ? '' : String(number));
            setMale(number > 0 ? String(100 - number) : '');
        }
    };

    const validate = (): string | null => {
        if (!title.trim()) return 'Handle is required.';
        if (visibleMetrics.some(key => (metrics[key] ?? '') === '')) return 'Fill in all channel metrics.';
        if (male === '' || female === '') return 'Gender split is required.';
        if (countries.some(item => !item.country || item.value === '')) {
            return 'Fill in all five top countries.';
        }
        if ((audienceMetricAge ?? []).some((metric: string) => (ages[metric] ?? '') === '')) {
            return 'Fill in all age groups.';
        }
        const sum = (values: string[]) => values.reduce((total, value) => total + Math.round(Number(value) || 0), 0);
        if (sum(Object.values(ages)) > 100) return 'Age groups cannot sum to more than 100%.';
        if (sum(countries.map(item => item.value)) > 100) return 'Countries cannot sum to more than 100%.';
        if (sum([male, female]) > 100) return 'Gender cannot sum to more than 100%.';
        if (new Set(countries.map(item => item.country)).size < countries.length) {
            return 'Duplicate countries are not allowed.';
        }
        if (!file) return 'Audience screenshot is required.';
        return null;
    };

    const save = () => {
        const error = validate();
        if (error) {
            WarningToast(error);
            return;
        }
        const metricValues: Record<string, number | string> = {};
        visibleMetrics.forEach(key => {
            metricValues[key] = key === 'allPlatforms' ? metrics[key] : Number(String(metrics[key]).replace(',', '.'));
        });
        const body = {
            title: title.trim().replace(/^@/, ''),
            externalLink: hasExternalLink ? externalLink : undefined,
            platformId: platform?.id,
            userId,
            followers: Number(metricValues.followers ?? 0),
            reach: 0,
            ...metricValues,
            engagementsRate: metricValues.engagementRate,
            audiences: [
                { dimension: 'gender', metric: 'male', value: Number(male) },
                { dimension: 'gender', metric: 'female', value: Number(female) },
                ...countries.map((item, index) => ({
                    dimension: 'country',
                    metric: item.country,
                    value: Number(item.value),
                    position: `position-${index + 1}`,
                })),
                ...(audienceMetricAge ?? []).map((metric: string) => ({
                    dimension: 'age',
                    metric,
                    value: Number(ages[metric]),
                })),
            ],
        };
        setIsSaving(true);
        UserService.saveManualAdvancedSocial(userId, platform?.id as number, file as File, body, isUpdate)
            .then(() => {
                SuccessToast(isUpdate ? 'Manual data updated' : 'Platform connected manually');
                onSaved();
            })
            .catch(err => ErrorToast(err))
            .finally(() => setIsSaving(false));
    };

    return (
        <>
            <BlockingOverlay show={isSaving} message={`Saving ${platform?.name ?? ''} data...`} />
            <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center gap-3">
                        {platform?.path && <img src={platform.path} alt={platform.name} width={33} />}
                        {platform?.name} - {isUpdate ? 'Manual update' : 'Manual connection'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h6 className="mb-3">Channel info</h6>
                    <Row className="mb-3">
                        <Form.Group as={Col} md={6} className="mb-3">
                            <Form.Label>Handle</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>@</InputGroup.Text>
                                <Form.Control
                                    value={title}
                                    disabled={isUpdate}
                                    onChange={event =>
                                        setTitle(event.target.value.replace(/\s/g, '').replace(/^@/, ''))
                                    }
                                />
                            </InputGroup>
                        </Form.Group>
                        {hasExternalLink && (
                            <Form.Group as={Col} md={6} className="mb-3">
                                <Form.Label>External link</Form.Label>
                                <Form.Control
                                    value={externalLink}
                                    onChange={event => setExternalLink(event.target.value)}
                                />
                            </Form.Group>
                        )}
                    </Row>

                    <h6 className="mb-3">Audience data</h6>
                    <Row className="mb-3">
                        <Form.Group as={Col} md={3} xs={6} className="mb-3">
                            <Form.Label>Male %</Form.Label>
                            <Form.Control
                                type="number"
                                min={0}
                                max={100}
                                value={male}
                                onChange={event => setGender('male', event.target.value)}
                            />
                        </Form.Group>
                        <Form.Group as={Col} md={3} xs={6} className="mb-3">
                            <Form.Label>Female %</Form.Label>
                            <Form.Control
                                type="number"
                                min={0}
                                max={100}
                                value={female}
                                onChange={event => setGender('female', event.target.value)}
                            />
                        </Form.Group>
                        {visibleMetrics.map(key => (
                            <Form.Group as={Col} md={3} xs={6} className="mb-3" key={key}>
                                <Form.Label>{labelFromKey(key)}</Form.Label>
                                <Form.Control
                                    type={key === 'allPlatforms' ? 'text' : 'number'}
                                    min={0}
                                    value={metrics[key] ?? ''}
                                    onChange={event => setMetrics(prev => ({ ...prev, [key]: event.target.value }))}
                                />
                            </Form.Group>
                        ))}
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <h6 className="mb-3">Top countries</h6>
                            {countries.map((item, index) => (
                                <Row key={index} className="mb-2 align-items-center">
                                    <Col xs={8}>
                                        <SingleSelectDropdown
                                            placeholder={`Country ${index + 1}`}
                                            withImage
                                            options={(country ?? []).map((code: string) => ({
                                                value: code,
                                                label: getCountryName(code),
                                                image: `/flags/4x3/${code.toLowerCase()}.svg`,
                                            }))}
                                            selected={item.country}
                                            onChange={value =>
                                                setCountries(prev =>
                                                    prev.map((entry, i) =>
                                                        i === index ? { ...entry, country: value } : entry
                                                    )
                                                )
                                            }
                                        />
                                    </Col>
                                    <Col xs={4}>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                min={0}
                                                max={100}
                                                value={item.value}
                                                onChange={event =>
                                                    setCountries(prev =>
                                                        prev.map((entry, i) =>
                                                            i === index
                                                                ? {
                                                                      ...entry,
                                                                      value:
                                                                          event.target.value === ''
                                                                              ? ''
                                                                              : String(toNumber(event.target.value)),
                                                                  }
                                                                : entry
                                                        )
                                                    )
                                                }
                                            />
                                            <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </Col>
                                </Row>
                            ))}
                        </Col>
                        <Col md={6}>
                            <h6 className="mb-3">Age groups</h6>
                            <Row>
                                {(audienceMetricAge ?? []).map((metric: string) => (
                                    <Form.Group as={Col} xs={6} className="mb-3" key={metric}>
                                        <Form.Label>{ageLabel(metric)} %</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={ages[metric] ?? ''}
                                            onChange={event =>
                                                setAges(prev => ({
                                                    ...prev,
                                                    [metric]:
                                                        event.target.value === ''
                                                            ? ''
                                                            : String(toNumber(event.target.value)),
                                                }))
                                            }
                                        />
                                    </Form.Group>
                                ))}
                            </Row>
                        </Col>
                    </Row>

                    <h6 className="mb-2">Verification screenshot</h6>
                    <p className="text-muted fs-7">Upload a screenshot of the audience statistics (JPG or PNG).</p>
                    {existingFile?.path && !file && (
                        <a href={existingFile.path} target="_blank" rel="noreferrer" className="d-inline-block mb-3">
                            <img
                                src={existingFile.path}
                                alt={existingFile.originalName}
                                style={{ width: 140, height: 100, objectFit: 'contain' }}
                            />
                        </a>
                    )}
                    <div
                        {...getRootProps()}
                        style={{ borderStyle: 'dashed' }}
                        className={`border rounded p-4 text-center cursor-pointer ${
                            isDragActive ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle'
                        }`}
                    >
                        <input {...getInputProps()} />
                        <FaUpload className="text-muted mb-2" size={20} />
                        <div className="fs-7 text-muted">Drag and drop an image here, or click to select</div>
                    </div>
                    {fileError && <div className="text-danger fs-8 mt-2">{fileError}</div>}
                    {file && (
                        <div className="d-flex align-items-center justify-content-between border rounded p-2 mt-3">
                            <span className="text-truncate">{file.name}</span>
                            <Button variant="" size="sm" onClick={() => setFile(null)} aria-label="Remove file">
                                <FaTimes />
                            </Button>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={onHide} disabled={isSaving}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={save} disabled={isSaving}>
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
